import { useEffect, useSyncExternalStore } from 'react'
import { fetchAllPages } from '../utils/fetchAllPages'
import type { EstateResponse } from '../utils/types'

/**
 * Que tipos de estate tem a carteira da empresa: só condomínios, só propriedades ou os dois. Os
 * ecrãs da empresa toda (Papéis, Definições, Módulos) falam conforme ela — ver i18n/vocabulario.ts.
 *
 * Um só pedido a `/api/estates`, partilhado. Só se pede com sessão iniciada (`enabled`): o provider
 * que o usa está montado também no login, e um 401 aí não pode acontecer. Refaz-se depois de criar
 * ou editar um estate e limpa-se no logout ({@link invalidateComposicaoEmpresa}). Os arquivados não
 * contam; sem estates, ou sem permissão para os listar, fica `null` (o vocabulário de sempre).
 */
export type ComposicaoEmpresa = 'CONDOMINIUM' | 'PROPERTIES' | 'MISTO'

interface Snapshot {
  estado: 'por-pedir' | 'a-pedir' | 'pedido'
  composicao: ComposicaoEmpresa | null
}

const POR_PEDIR: Snapshot = { estado: 'por-pedir', composicao: null }

let snapshot: Snapshot = POR_PEDIR
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

function carregar() {
  if (snapshot.estado !== 'por-pedir') return
  snapshot = { estado: 'a-pedir', composicao: null }
  fetchAllPages<EstateResponse>('/api/estates', { suppressForbiddenToast: true })
    .then((estates) => {
      const tipos = new Set(estates.filter((e) => e.status !== 'ARCHIVED').map((e) => e.stateType))
      const composicao: ComposicaoEmpresa | null = tipos.has('PROPERTIES')
        ? (tipos.has('CONDOMINIUM') ? 'MISTO' : 'PROPERTIES')
        : tipos.has('CONDOMINIUM') ? 'CONDOMINIUM' : null
      snapshot = { estado: 'pedido', composicao }
    })
    .catch(() => {
      snapshot = { estado: 'pedido', composicao: null }
    })
    .finally(emit)
}

/** Volta a pedir quando houver quem precise (logout, ou um estate criado/editado). */
export function invalidateComposicaoEmpresa() {
  snapshot = POR_PEDIR
  emit()
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function useComposicaoEmpresa(enabled: boolean): ComposicaoEmpresa | null {
  const s = useSyncExternalStore(subscribe, () => snapshot, () => snapshot)
  useEffect(() => {
    if (enabled && s.estado === 'por-pedir') carregar()
  }, [enabled, s.estado])
  return enabled ? s.composicao : null
}
